import React, { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Drawer as MuiDrawer,
  Grid2 as Grid,
  Typography,
  Paper,
} from "@mui/material";
import { ErrorOutlineOutlined, DoneOutlined } from "@mui/icons-material";
import Button from "./Button";
import { useAlertStyles } from "./css";

interface IAlert {
  isOpen: boolean;
  onClose: (event: {}) => void;
  onConfirm?: (event: {}) => void;
  onConfirmDisassemble?: (event: {}) => void;
  onConfirmDisassembleAndUpload?: (event: {}) => void;
  onConfirmUploadAll?: (event: {}) => void;
  iconColor?: "error" | "warning" | "success";
  isConfirmationButton?: boolean;
  message?: string;
  context?: string;
  onExited?: () => void;
  disableRestoreFocus?: boolean;
}

function Alert(props: IAlert) {
  const {
    isOpen = false,
    onClose,
    message,
    isConfirmationButton,
    onConfirm,
    onConfirmDisassemble,
    onConfirmDisassembleAndUpload,
    onConfirmUploadAll,
    iconColor,
    context,
    onExited,
    disableRestoreFocus,
  } = props;
  const { classes } = useAlertStyles();
  const [translate] = useTranslation();
  const messageArray = message ? message.split(/\n/) : [];
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if ("vibrate" in navigator) {
      window.navigator.vibrate(300);
    }
  }, []);

  useEffect(() => {
    if (isConfirmationButton) return;
    if (isOpen) {
      setTimeout(() => {
        buttonRef.current?.focus();
      }, 300);
    }
  }, [isOpen, buttonRef, isConfirmationButton]);

  const handleClose = (event: {}) => {
    localStorage.removeItem("btn1");
    localStorage.removeItem("btn2");
    localStorage.removeItem("btn3");
    onClose(event);
  };

  const btn1 = localStorage.getItem("btn1");
  const btn2 = localStorage.getItem("btn2");
  const btn3 = localStorage.getItem("btn3");

  return (
    <MuiDrawer
      open={isOpen}
      anchor="bottom"
      onClose={handleClose}
      elevation={0}
      variant="temporary"
      disableRestoreFocus={disableRestoreFocus}
      slotProps={{ transition: { onExited } }}
      classes={{ root: classes.root }}
    >
      <Paper elevation={0} className={classes.paper}>
        <Grid
          container
          justifyContent="space-around"
          direction="column"
          spacing={3}
        >
          <Grid container justifyContent="center">
            <Grid size={6} container justifyContent="center">
              {
                (btn1 || btn2 || btn3) && iconColor !== "error" ? (
                  // Jeigu yra mygtukai ir nėra klaidos, rodome oranžinę šauktuko ikoną
                  <ErrorOutlineOutlined
                    fontSize="large"
                    className={classes.iconWarning}
                  />
                ) : iconColor === "error" ? (
                  // Jeigu klaida, rodome raudoną šauktuko ikoną
                  <ErrorOutlineOutlined
                    fontSize="large"
                    className={classes.iconError}
                  />
                ) : iconColor === "warning" ? (
                  // Jeigu įspėjimas, rodome oranžinę šauktuko ikoną
                  <ErrorOutlineOutlined
                    fontSize="large"
                    className={classes.iconWarning}
                  />
                ) : iconColor === "success" ? (
                  // Jeigu sėkmė, rodome žalią ikoną
                  <DoneOutlined
                    fontSize="large"
                    className={classes.iconSuccess}
                  />
                ) : null // Jeigu nėra būsenos, nerodome nieko
              }
            </Grid>
          </Grid>

          <Grid container justifyContent="center">
            <Typography variant="body1" className={classes.typography}>
              {messageArray.map((line, index) => (
                <span key={index} style={{ display: "block" }}>
                  {line}
                </span>
              ))}
            </Typography>
          </Grid>

          <Grid container direction="column" spacing={2}>
            {isConfirmationButton && (
              <Grid
                container
                justifyContent="center"
                sx={{ width: "100%" }}
                spacing={2}
              >
                {btn1 && (
                  <Grid size={{ xs: 12, sm: "grow" }}>
                    <Button onClick={onConfirmDisassemble} fullWidth>
                      {btn1}
                    </Button>
                  </Grid>
                )}
                {btn2 && (
                  <Grid size={{ xs: 12, sm: "grow" }}>
                    <Button onClick={onConfirmDisassembleAndUpload} fullWidth>
                      {btn2}
                    </Button>
                  </Grid>
                )}
                {btn3 && (
                  <Grid size={{ xs: 12, sm: "grow" }}>
                    <Button onClick={onConfirmUploadAll} fullWidth>
                      {btn3}
                    </Button>
                  </Grid>
                )}
                {((!btn1 &&
                  !btn2 &&
                  !btn3 &&
                  localStorage.getItem("warningId")) ||
                  context === "versionChange" ||
                  context === "deleteRecord") && (
                  <Button onClick={onConfirm} fullWidth disableElevation>
                    {translate("continue", "Tęsti")}
                  </Button>
                )}
              </Grid>
            )}
            <Grid>
              <Button
                onClick={handleClose}
                fullWidth
                otherColors="white"
                disableElevation
                ref={buttonRef}
              >
                {translate("cancel", "Uždaryti")}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </MuiDrawer>
  );
}

export default Alert;
